import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const API_URL =
  "https://phpstack-1434504-6130285.cloudwaysapps.com/api/master-blogs?populate[image]=true&populate[og_image]=true&publicationState=live";

// URL ko split karke first segment se slug lo
const slugToTitle = (slug) =>
  (slug || "india")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase()); // Title Case

const withBase = (url) => {
  if (!url) return "";
  try {
    new URL(url);
    return url;
  } catch {
    return `https://phpstack-1434504-6130285.cloudwaysapps.com${url}`;
  }
};

function richToText(value) {
  if (!value) return "";
  const walk = (n) =>
    Array.isArray(n)
      ? n.map(walk).join(" ")
      : n?.text
      ? n.text
      : n?.children
      ? walk(n.children)
      : typeof n === "string"
      ? n
      : "";
  return walk(value).replace(/\s+/g, " ").trim();
}

function Searchbar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // 🔎 search
  const [q, setQ] = useState("");

  // 🔢 pagination
  const PER_PAGE = 10;
  const [page, setPage] = useState(1);

  // ✅ URL se location le lo
  const location = useLocation();
  const parts = location.pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  const locationSlug = parts[1] || "india";
  const locationName = slugToTitle(locationSlug); // e.g. "new-delhi" -> "New Delhi"

  // reset page on search change
  useEffect(() => {
    setPage(1);
  }, [q]);

  // smooth scroll to top on page change
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [page]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await fetch(API_URL, { headers: { "Content-Type": "application/json" } });
        const text = await res.text();
        if (!res.ok) throw new Error(`API ${res.status}: ${text || res.statusText}`);
        const json = text ? JSON.parse(text) : { data: [] };
        if (!cancelled) setItems(Array.isArray(json?.data) ? json.data : []);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load blogs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const blogs = useMemo(() => {
    return items.map((item) => {
      const a = item.attributes || item;

      // Base title
      const rawTitle = a.title || a.meta_title || `Untitled #${item.id}`;

      // ✅ {locationName} ko dynamic value se replace karo
      const title = rawTitle.replace(/\{locationName\}/g, locationName);

      const desc =
        (a.excerpt && a.excerpt.trim()) ||
        richToText(a.description) ||
        a.meta_description ||
        "";

      const imgData =
        (a.image && a.image.data) ||
        (a.og_image && a.og_image.data) ||
        a.image ||
        a.og_image;

      let imgUrl = "";
      if (Array.isArray(imgData)) {
        imgUrl = withBase(imgData[0]?.attributes?.url || imgData[0]?.url);
      } else if (imgData) {
        imgUrl = withBase(imgData?.attributes?.url || imgData?.url);
      }

      const createdAt = a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "";
      const slug = a.slug || item.id;

      // hidden blob for better search coverage
      const searchBlob = [title, desc].join(" ").toLowerCase();

      return { id: item.id, title, desc, imgUrl, createdAt, slug, searchBlob };
    });
  }, [items, locationName]);

  // filter
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return blogs;
    return blogs.filter(
      (b) =>
        b.title.toLowerCase().includes(needle) ||
        (b.desc || "").toLowerCase().includes(needle) ||
        (b.searchBlob || "").includes(needle)
    );
  }, [blogs, q]);

  // pagination slice
  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));
  const clampedPage = Math.min(page, pageCount);
  const start = (clampedPage - 1) * PER_PAGE;
  const current = filtered.slice(start, start + PER_PAGE);

  const goToPage = (p) => {
    if (p < 1 || p > pageCount) return;
    setPage(p);
  };

  const getPageNumbers = () => {
    const maxButtons = 5;
    if (pageCount <= maxButtons) {
      return Array.from({ length: pageCount }, (_, i) => i + 1);
    }
    const middle = Math.max(3, Math.min(clampedPage, pageCount - 2));
    const pages = [1, middle - 1, middle, middle + 1, pageCount]
      .filter((v, i, a) => a.indexOf(v) === i)
      .filter((v) => v >= 1 && v <= pageCount)
      .sort((a, b) => a - b);
    return pages;
  };

  const renderPagination = () => {
    if (total <= PER_PAGE) return null;
    const nums = getPageNumbers();
    return (
      <nav aria-label="Blogs pagination" className="d-flex justify-content-center mt-4">
        <ul className="pagination">
          <li className={`page-item ${clampedPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => goToPage(clampedPage - 1)}>
              Prev
            </button>
          </li>

          {/* left ellipsis */}
          {nums[0] > 1 && (
            <>
              <li className={`page-item ${clampedPage === 1 ? "active" : ""}`}>
                <button className="page-link" onClick={() => goToPage(1)}>1</button>
              </li>
              {nums[0] > 2 && (
                <li className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              )}
            </>
          )}

          {nums.map((n) => (
            <li key={n} className={`page-item ${n === clampedPage ? "active" : ""}`}>
              <button className="page-link" onClick={() => goToPage(n)}>
                {n}
              </button>
            </li>
          ))}

          {/* right ellipsis */}
          {nums[nums.length - 1] < pageCount && (
            <>
              {nums[nums.length - 1] < pageCount - 1 && (
                <li className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              )}
              <li className={`page-item ${clampedPage === pageCount ? "active" : ""}`}>
                <button className="page-link" onClick={() => goToPage(pageCount)}>
                  {pageCount}
                </button>
              </li>
            </>
          )}

          <li className={`page-item ${clampedPage === pageCount ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => goToPage(clampedPage + 1)}>
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div>
      <div id="colorlib-main">
        <section className="ftco-section ftco-no-pt ftco-no-pb">
          <div className="container sidebar-box pl-5 pt-4 pr-5 mb-0">
            <div className="row align-items-center  mb-3">
              <div className="col-md-4">
                <h2 className="h4 mb-0">Search Your Article...</h2>
              </div>

              {/* ✅ Stylish expanding search bar */}
              <div className="col-md-8 d-flex justify-content-end">
                <div className="modern-search mt-3"  >
                  <input
                    type="text"
                    className="modern-input"
                    placeholder="Search blogs..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    aria-label="Search blogs"
                  />
                  <span className="search-icon">
                    <i className="icon ri-search-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {loading && <p className="text-center py-5">Loading blogs...</p>}
          {err && <p className="text-center text-danger py-5">{err}</p>}
          {!loading && !err && total === 0 && (
            <p className="text-center py-5">
              {q ? <>No blogs matched <strong>"{q}"</strong>.</> : "No blogs found."}
            </p>
          )}

          {!loading && !err && total > 0 && (
            <>
              <div className="row p-5">
                {current.map((b) => (
                  <div className="col-md-4 mb-4" key={b.id}>
                    <div className="card h-100 shadow-sm">
                      {b.imgUrl ? (
                        <img
                          src={b.imgUrl}
                          alt={b.title}
                          className="card-img-top"
                          style={{ height: 200, objectFit: "cover" }}
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="bg-light d-flex align-items-center justify-content-center"
                          style={{ height: 200 }}
                        >
                          <span className="text-muted">No image</span>
                        </div>
                      )}
                      <div className="card-body d-flex flex-column">
                        {/* ✅ Ab title me dynamic location inserted hai */}
                        <h5 className="card-title">{b.title}</h5>

                        {/* Optional: show which location was used */}
                        {/* <p className="mb-2 small text-muted">{locationName}</p> */}

                        <Link className="btn btn-primary mt-auto" to={`/blog/${locationSlug}/${b.slug}`}>
                          Read More
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* pagination */}
              {renderPagination()}

              {/* range summary */}
              <div className="text-center mt-2 small text-muted">
                Showing {total === 0 ? 0 : start + 1}–{Math.min(start + current.length, total)} of {total}
              </div>
            </>
          )}
        </section>
      </div>

      {/* ✅ CSS Styling */}
      <style>{`
        .modern-search {
          position: relative;
          width: 45px;
          height: 45px;
          transition: all 0.4s ease;
          border-radius: 50px;
          background: #f1f3f6;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 0 rgba(0,0,0,0);
        }
        .modern-search:hover,
        .modern-search:focus-within {
          width: 100%;
          background: #fff;
          box-shadow: 0 0 10px rgba(13,110,253,0.3);
        }
        .modern-input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          padding: 10px 40px 10px 15px;
          opacity: 0;
          font-size: 15px;
          transition: opacity 0.3s ease;
        }
        .modern-search:hover .modern-input,
        .modern-search:focus-within .modern-input {
          opacity: 1;
        }
        .search-icon {
          position: absolute;
          right: 12px;
          color: #e04495;
          font-size: 1.2rem;
          pointer-events: none;
          transition: transform 0.3s ease;
        }
        .modern-search:hover .search-icon,
        .modern-search:focus-within .search-icon {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  );
}

export default Searchbar;
